import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  TableChart as TableChartIcon,
  AutoGraph as AutoGraphIcon,
} from '@mui/icons-material';
import MisInformesHubSection from '../../components/shared/MisInformesHubSection';

const ComprasInformes: React.FC = () => {
  const navigate = useNavigate();

  const informes = [
    {
      title: 'Cuentas por Pagar',
      description: 'Visualiza el saldo pendiente de pago por proveedor.',
      icon: AssessmentIcon,
      path: 'cuentas-pagar',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Resumen de Cuenta',
      description: 'Visualiza la composición del saldo con todos los comprobantes del proveedor.',
      icon: AccountBalanceIcon,
      path: 'resumen-cuenta',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Libro IVA Compras',
      description: 'Visualiza el registro de IVA de todas las facturas de compra.',
      icon: ReceiptIcon,
      path: 'libro-iva-compras',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Informe dinámico de compras',
      description: 'Tabla Pivot: arrastrá campos, filtros y totales.',
      icon: TableChartIcon,
      path: 'compras-dinamico',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Seguimiento de compras',
      description: 'Pendientes de facturar/recibir y hilo de comprobantes por renglón master.',
      icon: TableChartIcon,
      path: 'seguimiento-compras',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Sugerencia de compra',
      description: 'Analiza stock, ventas y pendientes para proponer compras por proveedor.',
      icon: AutoGraphIcon,
      path: 'sugerencia-compra',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                mb: 1
              }}
            >
              Informes de Compras
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.95rem' }}
            >
              Selecciona un informe para visualizar información detallada
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Informes estándar
          </Typography>
          <Grid container spacing={2.5}>
            {informes.map((informe) => {
              const IconComponent = informe.icon;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={informe.path}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: 180, sm: 195 },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        borderColor: informe.color,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(informe.path)}
                      sx={{
                        p: 0,
                        height: '100%',
                        alignItems: 'stretch',
                        '&:hover': {
                          '& .MuiCardActionArea-focusHighlight': {
                            opacity: 0,
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.25 }}>
                        <Stack spacing={1.5}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: informe.iconBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 27,
                                color: informe.color,
                              }}
                            />
                          </Box>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              mb: 0.5,
                            }}
                          >
                            {informe.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.975rem',
                              lineHeight: 1.6,
                            }}
                          >
                            {informe.description}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: informe.color,
                              fontWeight: 500,
                              fontSize: '0.85rem',
                            }}
                          >
                            Ver informe
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                transition: 'transform 0.3s ease',
                                display: 'inline-block',
                                '&:hover': {
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              →
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <MisInformesHubSection titulo="Mis informes de compra" />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ComprasInformes;




